import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './PaymentModal.css';

interface PaymentModalProps {
  beneficiary: any;
  onClose: () => void;
  onPaymentInitiated: (paymentData: any) => void;
}

const PaymentModal: React.FC<PaymentModalProps> = ({ beneficiary, onClose, onPaymentInitiated }) => {
  const [amount, setAmount] = useState('');
  const [reference, setReference] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [step, setStep] = useState<'amount' | 'confirm' | 'success'>('amount');
  const [showSuccess, setShowSuccess] = useState(false);

  const handleAmountSubmit = () => {
    if (!amount || parseFloat(amount) <= 0) {
      return;
    }
    setStep('confirm');
  };

  const handlePayment = async () => {
    setIsProcessing(true);

    try {
      const paymentData = {
        beneficiaryId: beneficiary.id,
        amount: parseFloat(amount),
        reference: reference || `Payment to ${beneficiary.name}`,
        paymentType: 'standard',
        timestamp: new Date().toISOString()
      };

      // Simulate payment processing
      await new Promise(resolve => setTimeout(resolve, 1500));

      // In production, make API call to payment service
      const response = await fetch('/api/process-payment', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(paymentData),
      });

      if (response.ok || true) { // Always succeed for demo
        setStep('success');
        setShowSuccess(true);
        
        // Auto-close after success animation
        setTimeout(() => {
          onPaymentInitiated(paymentData);
          onClose();
        }, 2000);
      } else {
        throw new Error('Payment failed');
      }
    } catch (error) {
      console.error('Payment error:', error);
      alert('Payment failed. Please try again.');
      setStep('confirm');
    } finally {
      setIsProcessing(false);
    }
  };

  const formatCurrency = (value: string) => {
    const num = parseFloat(value);
    return isNaN(num) ? '0.00' : num.toFixed(2);
  };

  const formatAmount = (value: string) => {
    // Remove non-numeric characters except decimal point
    const cleaned = value.replace(/[^\d.]/g, '');
    const parts = cleaned.split('.');
    if (parts.length > 2) {
      return parts[0] + '.' + parts[1];
    }
    return cleaned;
  };

  return (
    <motion.div
      className="payment-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        className="payment-modal"
        initial={{ scale: 0.9, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.9, opacity: 0, y: 20 }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <motion.button 
            className="close-btn" 
            onClick={onClose}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
          >
            ×
          </motion.button>
        </div>

        <AnimatePresence mode="wait">
          {step === 'amount' && (
            <motion.div
              key="amount"
              className="step-content"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              <div className="beneficiary-header">
                <div className="beneficiary-avatar">
                  {beneficiary.name.split(' ').map((n: string) => n[0]).join('').toUpperCase()}
                </div>
                <div className="beneficiary-info">
                  <h3>{beneficiary.name}</h3>
                  <p>{beneficiary.accountNumber}</p>
                </div>
              </div>

              <div className="amount-section">
                <label className="amount-label">Amount</label>
                <div className="amount-input-container">
                  <span className="currency">R</span>
                  <input
                    type="text"
                    value={amount}
                    onChange={(e) => setAmount(formatAmount(e.target.value))}
                    placeholder="0.00"
                    className="amount-input"
                    autoFocus
                    onKeyPress={(e) => e.key === 'Enter' && handleAmountSubmit()}
                  />
                </div>
                {amount && (
                  <motion.div 
                    className="amount-display"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    R {formatCurrency(amount)}
                  </motion.div>
                )}
              </div>

              <div className="reference-section">
                <input
                  type="text"
                  value={reference}
                  onChange={(e) => setReference(e.target.value)}
                  placeholder="Payment reference (optional)"
                  className="reference-input"
                />
              </div>

              <motion.button
                className={`continue-btn ${!amount || parseFloat(amount) <= 0 ? 'disabled' : ''}`}
                onClick={handleAmountSubmit}
                disabled={!amount || parseFloat(amount) <= 0}
                whileHover={amount && parseFloat(amount) > 0 ? { scale: 1.02 } : {}}
                whileTap={amount && parseFloat(amount) > 0 ? { scale: 0.98 } : {}}
              >
                Continue
              </motion.button>
            </motion.div>
          )}

          {step === 'confirm' && (
            <motion.div
              key="confirm"
              className="step-content"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              <div className="confirm-header">
                <h2>Confirm Payment</h2>
              </div>

              <div className="payment-summary">
                <div className="summary-row">
                  <span>To</span>
                  <span>{beneficiary.name}</span>
                </div>
                <div className="summary-row">
                  <span>Account</span>
                  <span>{beneficiary.accountNumber}</span>
                </div>
                <div className="summary-row">
                  <span>Reference</span>
                  <span>{reference || `Payment to ${beneficiary.name}`}</span>
                </div>
                <div className="summary-row amount-row">
                  <span>Amount</span>
                  <span className="amount">R {formatCurrency(amount)}</span>
                </div>
              </div>

              <div className="confirm-actions">
                <motion.button
                  className="back-btn"
                  onClick={() => setStep('amount')}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Back
                </motion.button>
                <motion.button
                  className="pay-btn"
                  onClick={handlePayment}
                  disabled={isProcessing}
                  whileHover={!isProcessing ? { scale: 1.02 } : {}}
                  whileTap={!isProcessing ? { scale: 0.98 } : {}}
                >
                  {isProcessing ? (
                    <>
                      <div className="loading-spinner"></div>
                      Processing...
                    </>
                  ) : (
                    `Pay R ${formatCurrency(amount)}`
                  )}
                </motion.button>
              </div>
            </motion.div>
          )}

          {step === 'success' && (
            <motion.div
              key="success"
              className="step-content success-content"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4 }}
            >
              <motion.div 
                className="success-icon"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", delay: 0.2, damping: 15, stiffness: 300 }}
              >
                ✓
              </motion.div>
              <h2>Payment Sent!</h2>
              <p>R {formatCurrency(amount)} sent to {beneficiary.name}</p>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </motion.div>
  );
};
export default PaymentModal;